import * as React from 'react';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { escapeQuotes } from '@/utils/escapeQuotes';
import { getAuctioneerLogoUrl, getImageUrl, getItemUrlFromSlug } from '@/utils/urls';
import { getBaseUrl } from '@/redux/modules/config';
import { getCatalogAddress, getCatalogSellerName } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCategoryLevels, getCategoryLevelsString } from '@/utils/categoryGrabber';
import { getDisplayRating, getSellerRatingTotalReviews } from '@/redux/modules/sellerRatings';
import { getItemDetailDescription } from '@/redux/modules/itemDetail';
import { getItemFacets } from '@/redux/modules/itemFacets';
import { getItemIsRestricted, getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLocale } from '@/redux/modules/intl';
import { getSeller } from '@/redux/modules/seller';
import { safelyEscapeText } from '@/utils/safelyRender';
import { shortFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';

const FOUR_HOURS = 14400000;

type Props = {
    itemId: number;
};

const ItemPageHeaderMeta = ({ itemId }: Props) => {
    const baseUrl = useAppSelector(getBaseUrl);
    const {
        buyNowPrice,
        buyNowStatus,
        catalogId,
        catalogStatus,
        currency,
        imageVersion,
        isPassed,
        isTimedPlusAuction,
        leadingBid,
        lotEndTimeEstimatedTs,
        lowBidEstimate,
        photos = [],
        salePrice,
        saleStartTs,
        sellerId,
        slugWithLocation,
        startPrice,
        title: itemTitle = '',
    } = useAppSelector((state) => getItemSummary(state, itemId));

    const { logoId } = useAppSelector((state) => getSeller(state, sellerId));
    const itemDescription = useAppSelector((state) => getItemDetailDescription(state, itemId));
    const facets = useAppSelector((state) => getItemFacets(state, itemId));
    const { categories, creators } = facets;
    const creator = creators?.[0] || null;
    const address = useAppSelector((state) => getCatalogAddress(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const endedAuction = catalogStatus === AuctionStatus.Done;
    const locale = useAppSelector(getLocale);
    const { formatMessage } = useIntl();
    const isRestricted = useAppSelector((state) => getItemIsRestricted(state, itemId));

    const displayRating = useAppSelector((state) => getDisplayRating(state, sellerId));
    const totalReviews = useAppSelector((state) => getSellerRatingTotalReviews(state, sellerId));

    const getLdMarkup = (data: any) => {
        return <script type="application/ld+json">{JSON.stringify(data)}</script>;
    };

    const getLdData = React.useCallback(() => {
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [],
        };
    }, []);

    const getLdBreadcrumbs = React.useCallback(
        (categories: any[], currentUrl: string, title: string) => {
            if (!categories || !categories.length) {
                return;
            }
            let categoryCount = 0;
            const ldData = getLdData();
            ldData.itemListElement.push({
                '@type': 'ListItem',
                item: {
                    '@id': `${baseUrl}`,
                    name: 'Home',
                },
                position: categoryCount++,
            });
            const categoryLevels = getCategoryLevels(categories[0]);
            while (categoryCount <= categoryLevels.length) {
                ldData.itemListElement.push({
                    '@type': 'ListItem',
                    item: {
                        '@id': `${baseUrl}/c/${
                            categoryLevels[categoryCount - 1].categoryUrl
                        }/${categoryLevels[categoryCount - 1].categoryId}/`,
                        name: categoryLevels[categoryCount - 1].categoryName,
                    },
                    position: categoryCount++,
                });
            }
            ldData.itemListElement.push({
                '@type': 'ListItem',
                item: {
                    '@id': currentUrl,
                    name: escapeQuotes(title),
                },
                position: categoryCount++,
            });
            return getLdMarkup(ldData);
        },
        [baseUrl, getLdData]
    );

    const getLdOffer = React.useCallback(
        ({
            category,
            currency,
            isTimedPlusAuction,
            price,
            saleStart,
            timedPlusSaleEnd,
            title,
            url,
        }: {
            category: string;
            currency: string;
            isTimedPlusAuction: boolean;
            price: any;
            saleStart: number;
            timedPlusSaleEnd: number;
            title: string;
            url: string;
        }) => {
            // hardcoding InStock and only showing availability fields when available
            // const available = availability === 'instock' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
            const estimatedEndTime = isTimedPlusAuction ? timedPlusSaleEnd : saleStart + FOUR_HOURS;
            const unavailable = new Date(estimatedEndTime * 1000);
            const cleanTitle = escapeQuotes(title);

            return {
                '@type': 'Offer',
                availability: 'https://schema.org/InStock',
                availabilityEnds: unavailable,
                category,
                itemCondition: 'https://schema.org/UsedCondition',
                itemOffered: cleanTitle,
                price,
                priceCurrency: currency,
                priceValidUntil: unavailable,
                url,
            };
        },
        []
    );

    const getLdProduct = React.useCallback(
        (description: string, image: string, name: string, offer: any, creator: any, itemId: number) => {
            const cleanDescription = escapeQuotes(description);
            const cleanName = escapeQuotes(name);
            let product = {
                '@context': 'https://schema.org',
                '@type': 'Product',
                ...(Boolean(creator) && { brand: creator }),
                description: cleanDescription,
                image,
                name: cleanName,
                offers: offer,
                sku: itemId,
            };

            return getLdMarkup(product);
        },
        []
    );

    const getLdOrganization = React.useCallback(
        (
            sellerName: string,
            sellerId: number,
            address: { city: string; country: string; state: string },
            logoUrl: string,
            rating: number,
            reviewCount: number
        ) => {
            const organization = {
                '@context': 'https://schema.org',
                '@type': 'Organization',
                address: {
                    '@type': 'PostalAddress',
                    addressCountry: address.country,
                    addressLocality: address.city,
                    addressRegion: address.state,
                },
                aggregateRating: {
                    '@type': 'AggregateRating',
                    bestRating: 5,
                    ratingValue: rating,
                    reviewCount: reviewCount,
                    worstRating: 1,
                },
                location: {
                    '@type': 'Place',
                    address: {
                        '@type': 'PostalAddress',
                        addressCountry: address.country,
                        addressLocality: address.city,
                        addressRegion: address.state,
                    },
                    name: `${sellerName} Location`,
                },
                logo: logoUrl,
                name: sellerName,
                review: [
                    {
                        '@type': 'Review',
                        author: {
                            '@type': 'Organization',
                            name: 'LiveAuctioneers',
                        },
                        reviewRating: {
                            '@type': 'Rating',
                            bestRating: 5,
                            ratingValue: rating,
                            worstRating: 1,
                        },
                    },
                ],
                url: `${baseUrl}/seller/${sellerId}`,
            };

            return getLdMarkup(organization);
        },
        [baseUrl]
    );

    const getPrice = React.useCallback((price: number, locale: string, currency: string) => {
        try {
            return Intl.NumberFormat(locale, {
                currency,
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
                style: 'currency',
            }).format(price);
        } catch (error) {
            console.log(
                `Could not use ${locale} as a valid number format locale, or ${currency} as a valid currency, defaulting to "en" and "usd"`
            );
            return Intl.NumberFormat('en', {
                currency: 'USD',
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
                style: 'currency',
            }).format(price);
        }
    }, []);

    const { fullCountryName: country, state: stateCode } = address;
    const safeItemTitle = safelyEscapeText(itemTitle.trim());
    const saleDate = shortFormatDate(saleStartTs);

    const isBuyNow = buyNowStatus === 1;
    const buyNowPriceString = buyNowPrice ? getPrice(buyNowPrice, locale, currency) : '';

    const location = stateCode ? stateCode : country;
    const sellerNameAndLocationString = `| ${sellerName} in ${location}`;

    const unsafeTitle = !endedAuction
        ? safeItemTitle
        : isBuyNow
          ? formatMessage({ id: 'item_buy_now_title' }, { itemTitle: safeItemTitle, sellerNameAndLocationString })
          : `${safeItemTitle} - ${saleDate} ${sellerNameAndLocationString}`;

    // replace all whitespace with single spaces to avoid long
    const safeItemDescription = safelyEscapeText(itemDescription);

    const maxDescriptionLength = 155;
    const ellipsis = '...';
    const truncatedItemDescription =
        safeItemDescription?.length > maxDescriptionLength
            ? `${safeItemDescription.substring(0, maxDescriptionLength - ellipsis.length)}{ellipsis}`
            : safeItemDescription;

    const buyNowDescriptionString = formatMessage(
        { id: 'item_buy_now_description' },
        { buyNowPrice: buyNowPriceString }
    );
    const soldDescriptionString = formatMessage({ id: 'item_sold_description' }, { saleDate });
    const passedDescriptionString = formatMessage({
        id: 'item_passed_description',
    });

    const description = !endedAuction
        ? `${truncatedItemDescription}`
        : isBuyNow
          ? `${truncatedItemDescription} ${buyNowDescriptionString}`
          : isPassed
            ? `${truncatedItemDescription} ${passedDescriptionString}`
            : `${truncatedItemDescription} ${soldDescriptionString}`;

    let offerPrice = startPrice;
    if (isPassed) {
        offerPrice = lowBidEstimate;
    } else if (endedAuction && isBuyNow) {
        offerPrice = buyNowPrice;
    } else if (endedAuction) {
        offerPrice = salePrice;
    }

    const safeDescription = safelyEscapeText(description);
    const imageUrl = getImageUrl({
        catalogId,
        imageNumber: photos[0],
        imageVersion,
        itemId,
        sellerId,
    });
    const title = useAddNotificationCountToTitle(safelyEscapeText(unsafeTitle))
        // title casing const title
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    const itemPath = getItemUrlFromSlug(itemId, slugWithLocation);
    const canonical = `${baseUrl}${itemPath}`;
    const ldBreadcrumbs = getLdBreadcrumbs(categories, canonical, safeItemTitle);
    const categoryLevels = categories ? getCategoryLevels(categories[0]) : false;
    const categoryString = categoryLevels ? getCategoryLevelsString(categoryLevels) : '';
    const lowestCategory =
        categoryLevels && categoryLevels.length && categoryLevels[categoryLevels.length - 1]
            ? categoryLevels[categoryLevels.length - 1].categoryName
            : false;

    const availability = endedAuction && buyNowStatus !== BuyNowStatus.Available ? 'oos' : 'instock';
    const creatorValue = creator?.l1CategoryName || false;
    const ogPrice = endedAuction ? (isBuyNow ? buyNowPrice : salePrice) : Math.max(leadingBid, startPrice);
    const offer = getLdOffer({
        category: categoryString,
        currency,
        isTimedPlusAuction,
        price: offerPrice,
        saleStart: saleStartTs,
        timedPlusSaleEnd: lotEndTimeEstimatedTs,
        title,
        url: canonical,
    });

    const ldProduct = getLdProduct(safeDescription, imageUrl, safeItemTitle, offer, creatorValue, itemId);
    const isNotPublished = !['done', 'live', 'online'].includes(catalogStatus);

    // Add organization schema with review and place
    const ldOrganization = getLdOrganization(
        sellerName,
        sellerId,
        { city: address.city, country: address.country, state: address.state },
        getAuctioneerLogoUrl(logoId),
        displayRating,
        totalReviews
    );

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={safeDescription}
            imageUrl={imageUrl}
            title={formatMessage({ id: 'itemPage.meta.titleAppendAuction' }, { hasEnded: endedAuction, title })}
        >
            {isNotPublished && (
                <meta
                    content="noindex, nofollow"
                    name="robots"
                />
            )}

            {ldProduct}
            {ldBreadcrumbs}
            {ldOrganization}

            {!endedAuction && creatorValue && (
                <meta
                    content={creatorValue}
                    property="product:brand"
                />
            )}
            {isRestricted && (
                <meta
                    content="adult"
                    name="rating"
                />
            )}
            <meta property="og:image:height" />
            <meta property="og:image:width" />
            {!endedAuction && (
                <meta
                    content={availability}
                    property="product:availability"
                />
            )}
            {((endedAuction && isBuyNow) || !endedAuction) && (
                <meta
                    content={`${ogPrice}`}
                    property="product:price:amount"
                />
            )}
            {!endedAuction && (
                <meta
                    content={currency}
                    property="product:price:currency"
                />
            )}
            {!endedAuction && lowestCategory && (
                <meta
                    content={lowestCategory}
                    property="product:category"
                />
            )}

            <meta
                content="LiveAuctioneers"
                property="og:site_name"
            />

            {!endedAuction && (
                <meta
                    content="product"
                    property="og:type"
                />
            )}
        </PageHeaderMeta>
    );
};

export default React.memo<Props>(ItemPageHeaderMeta);
